import React, { useState, useEffect, Fragment } from 'react';
import LoaderScreen from '../layout/LoaderScreen';
import Moment from 'moment';
import { ReembolsoSevices } from '../../Services';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const styles = {
    pageContainer: {
        paddingTop: '10px',
        paddingBottom: '60px',
        maxWidth: '960px',
        margin: '0 auto',
    },
    pageHeader: {
        marginBottom: '32px',
    },
    pageTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#565657',
        marginBottom: '6px',
    },
    pageSubtitle: {
        fontSize: '16px',
        color: '#565657',
        lineHeight: '1.6',
    },
    sectionCard: {
        background: '#ffffff',
        borderRadius: '8px',
        padding: '28px',
        marginBottom: '20px',
        border: '1px solid #d0d0d0',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#565657',
        marginBottom: '4px',
    },
    sectionDescription: {
        fontSize: '16px',
        color: '#565657',
        marginBottom: '20px',
        lineHeight: '1.5',
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #e0e0e0',
        margin: '0 0 20px 0',
    },
    ownershipToggle: {
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
    },
    ownershipOption: (isActive) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 16px',
        borderRadius: '8px',
        border: isActive ? '2px solid #cd171a' : '2px solid #d0d0d0',
        background: isActive ? '#fdf2f2' : '#fafafa',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    }),
    ownershipLabel: (isActive) => ({
        fontSize: '17px',
        fontWeight: '700',
        color: isActive ? '#cd171a' : '#565657',
        marginBottom: '4px',
    }),
    ownershipDesc: (isActive) => ({
        fontSize: '15px',
        color: isActive ? '#cd171a' : '#565657',
        textAlign: 'center',
        lineHeight: '1.4',
    }),
    formLabel: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#565657',
        marginBottom: '6px',
        display: 'block',
    },
    required: {
        color: '#cd171a',
        fontWeight: '700',
    },
    formInput: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #d0d0d0',
        borderRadius: '6px',
        fontSize: '16px',
        color: '#565657',
        background: '#fff',
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    formSelect: {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #d0d0d0',
        borderRadius: '6px',
        fontSize: '16px',
        color: '#565657',
        background: '#fff',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23565657' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: 'right 10px center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '18px',
        cursor: 'pointer',
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    errorText: {
        fontSize: '15px',
        color: '#cd171a',
        marginTop: '4px',
    },
    fieldHint: {
        fontSize: '14px',
        color: '#565657',
        marginTop: '4px',
    },
    fileUploadZone: (hasFile) => ({
        border: hasFile ? '2px solid #28a745' : '2px dashed #d0d0d0',
        borderRadius: '8px',
        padding: '24px 16px',
        textAlign: 'center',
        cursor: 'pointer',
        background: hasFile ? '#f6fef9' : '#fafafa',
        transition: 'all 0.2s ease',
        position: 'relative',
        minHeight: '140px',
    }),
    fileUploadLabel: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#565657',
        marginBottom: '6px',
    },
    fileUploadHint: {
        fontSize: '15px',
        color: '#565657',
    },
    fileUploadName: {
        fontSize: '16px',
        color: '#28a745',
        fontWeight: '600',
        marginTop: '8px',
    },
    fileInput: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        cursor: 'pointer',
    },
    downloadLink: {
        display: 'inline-block',
        fontSize: '15px',
        fontWeight: '600',
        color: '#cd171a',
        textDecoration: 'underline',
        marginTop: '8px',
    },
    docGroupCard: {
        background: '#f8f8f8',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid #e0e0e0',
    },
    docGroupTitle: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#565657',
        marginBottom: '16px',
        paddingBottom: '10px',
        borderBottom: '1px solid #e0e0e0',
    },
    submitSection: {
        textAlign: 'center',
        padding: '32px 20px 20px',
    },
    submitButton: {
        background: '#cd171a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '14px 40px',
        fontSize: '17px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'background 0.2s ease',
        minWidth: '260px',
    },
    submitHint: {
        fontSize: '15px',
        color: '#565657',
        marginTop: '10px',
    },
    infoBanner: {
        padding: '12px 16px',
        background: '#fdf2f2',
        borderRadius: '6px',
        border: '1px solid #f0d0d0',
        marginBottom: '20px',
        fontSize: '15px',
        color: '#565657',
        lineHeight: '1.5',
    },
    warningBanner: {
        padding: '10px 16px',
        background: '#fef9e7',
        borderRadius: '6px',
        border: '1px solid #f0e0a0',
        marginBottom: '20px',
        fontSize: '15px',
        color: '#565657',
    },
};

const FileUploadCard = ({ label, onChange, fileName, required, t, downloadFormatUrl }) => (
    <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 w-100" style={styles.fileUploadZone(!!fileName)}>
        <input type="file" accept="image/jpeg,application/pdf" onChange={onChange} style={styles.fileInput} />
        <div style={styles.fileUploadLabel}>
            {required && <span style={styles.required}>* </span>}
            {label}
        </div>
        {fileName ? (
            <div style={styles.fileUploadName}>
                {fileName.length > 28 ? fileName.substring(0, 25) + '...' : fileName}
            </div>
        ) : (
            <div style={styles.fileUploadHint}>
                {t('paso4.clicSeleccionar', 'Clic para seleccionar archivo')}
                <br />
                <span style={{ fontSize: '14px' }}>{t('paso4.formatInfo', 'PDF, JPG — máx. 500KB')}</span>
            </div>
        )}
        {downloadFormatUrl && (
            <a
                style={{ ...styles.downloadLink, position: 'relative', zIndex: 10, marginTop: '12px' }}
                target="_blank"
                rel="noopener noreferrer"
                href={downloadFormatUrl}
                onClick={(e) => e.stopPropagation()}
            >
                {t('paso4.descargarFormato', 'Descargar formato')}
            </a>
        )}
    </div>
);

const FormField = ({ label, required, error, hint, children }) => (
    <div style={{ marginBottom: '18px' }}>
        <label style={styles.formLabel}>
            {required && <span style={styles.required}>* </span>}
            {label}
        </label>
        {children}
        {error && <div style={styles.errorText}>{error}</div>}
        {hint && !error && <div style={styles.fieldHint}>{hint}</div>}
    </div>
);

const Paso4 = (props) => {
    const { t } = useTranslation();
    const [ActivarLoader, setActivarLoader] = useState(false);
    const [MensajeLoader, setMensajeLoader] = useState("");
    const [FormaPagoC] = useState([2, 8]);

    const [nombresReceptor, setNombresReceptor] = useState("");
    const [apellidosReceptor, setApellidosReceptor] = useState("");
    const [paisOrigenDocReceptor, setPaisOrigenDocReceptor] = useState("");
    const [tipoDocReceptor, setTipoDocReceptor] = useState("");
    const [numeroDocReceptor, setNumeroDocReceptor] = useState("");

    const [direccionPagador, setDireccionPagador] = useState("");
    const [esTitularCuenta, setEsTitularCuenta] = useState(true);
    const [habilitarCamposReceptor, setHabilitarCamposReceptor] = useState(false);

    const [docIdentidadTitularFile, setDocIdentidadTitularFile] = useState("");
    const [docIdentidadTitularExt, setDocIdentidadTitularExt] = useState("");
    const [cuentaBancariaTitularFile, setCuentaBancariaTitularFile] = useState("");
    const [cuentaBancariaTitularExt, setCuentaBancariaTitularExt] = useState("");
    const [declaracionJuradaTitularFile, setDeclaracionJuradaTitularFile] = useState("");
    const [declaracionJuradaTitularExt, setDeclaracionJuradaTitularExt] = useState("");

    const [docIdentidadTerceroFile, setDocIdentidadTerceroFile] = useState("");
    const [docIdentidadTerceroExt, setDocIdentidadTerceroExt] = useState("");
    const [autorizacionTerceroFile, setAutorizacionTerceroFile] = useState("");
    const [autorizacionTerceroExt, setAutorizacionTerceroExt] = useState("");

    const [aba, setAba] = useState("");
    const [swift, setSwift] = useState("");

    const [errorNombresReceptorMsg, setErrorNombresReceptorMsg] = useState("");
    const [errorApellidosReceptorMsg, setErrorApellidosReceptorMsg] = useState("");

    const [paisesList, setPaisesList] = useState([]);
    const [documentosPorPaisList, setDocumentosPorPaisList] = useState([]);

    const [monedaNacional] = useState("VEF");
    const [mostrarCamposAbaSwift, setMostrarCamposAbaSwift] = useState(false);
    const [flujoConDocumentosPaso1, setFlujoConDocumentosPaso1] = useState(false);

    const [nameDocIdentidadTitular, setNameDocIdentidadTitular] = useState("");
    const [nameCuentaBancariaTitular, setNameCuentaBancariaTitular] = useState("");
    const [nameDeclaracionJuradaTitular, setNameDeclaracionJuradaTitular] = useState("");
    const [nameDocIdentidadTercero, setNameDocIdentidadTercero] = useState("");
    const [nameAutorizacionTercero, setNameAutorizacionTercero] = useState("");

    const [focusedField, setFocusedField] = useState(null);
    const [solicitudEnviada, setSolicitudEnviada] = useState(false);

    const esSoloTexto = (inputtxt) => /^[A-Za-zÀ-ÿ\s']+$/.test(inputtxt);

    const handleNombresReceptorChange = (valor) => {
        if (valor.length <= 50) {
            setNombresReceptor(valor);
            if (valor.trim() === "") setErrorNombresReceptorMsg(t('validaciones.campoObligatorio', "Campo obligatorio"));
            else if (!esSoloTexto(valor)) setErrorNombresReceptorMsg(t('validaciones.soloTexto', "Solo se permiten letras"));
            else setErrorNombresReceptorMsg("");
            window.localStorage.setItem("NombresReceptorPaso4", valor);
        }
    };

    const handleApellidosReceptorChange = (valor) => {
        if (valor.length <= 50) {
            setApellidosReceptor(valor);
            if (valor.trim() === "") setErrorApellidosReceptorMsg(t('validaciones.campoObligatorio', "Campo obligatorio"));
            else if (!esSoloTexto(valor)) setErrorApellidosReceptorMsg(t('validaciones.soloTexto', "Solo se permiten letras"));
            else setErrorApellidosReceptorMsg("");
            window.localStorage.setItem("ApellidosReceptorPaso4", valor);
        }
    };

    const handlePaisOrigenDocReceptorChange = (valor) => {
        setPaisOrigenDocReceptor(valor);
        setTipoDocReceptor("");
        setDocumentosPorPaisList([]);
        if (valor) {
            fetchDocumentosPorPais(valor);
            window.localStorage.setItem("ReceptorPaisDocumentoPaso4", valor);
        } else {
            window.localStorage.removeItem("ReceptorPaisDocumentoPaso4");
        }
    };

    const handleTipoDocReceptorChange = (valor) => {
        setTipoDocReceptor(valor);
        if (valor) window.localStorage.setItem("ReceptorTipoDocumentoPaso4", valor);
        else window.localStorage.removeItem("ReceptorTipoDocumentoPaso4");
    };

    const handleNumeroDocReceptorChange = (valor) => {
        if (valor.length <= 20) {
            setNumeroDocReceptor(valor);
            window.localStorage.setItem("ReceptorNumeroDocumentoPaso4", valor);
        }
    };

    const handleDireccionPagadorChange = (valor) => {
        setDireccionPagador(valor);
        if (valor.length >= 10) window.localStorage.setItem("DireccionPagadorPaso4", valor);
        else window.localStorage.removeItem("DireccionPagadorPaso4");
    };

    const handleAbaChange = (valor) => {
        setAba(valor);
        window.localStorage.setItem("AbaPaso4", valor);
    };

    const handleSwiftChange = (valor) => {
        setSwift(valor);
        window.localStorage.setItem("SwiftPaso4", valor);
    };

    const handleEsTitularCuentaChange = (event) => {
        const esTitular = event.target.value === "true";
        setEsTitularCuenta(esTitular);
        window.localStorage.setItem("EsTitularPaso4", esTitular.toString());
        setHabilitarCamposReceptor(!esTitular);

        if (esTitular) {
            const nombresPax = window.localStorage.getItem("Nombres") || "";
            const apellidosPax = window.localStorage.getItem("Apellidos") || "";
            const paisDocPax = window.localStorage.getItem("PasajeroPaisDocumento") || "";
            const tipoDocPax = window.localStorage.getItem("PasajeroTipoDocumentoSolicitante") || "";
            const numDocPax = window.localStorage.getItem("PasajeroDocumento") || "";

            setNombresReceptor(nombresPax);
            setApellidosReceptor(apellidosPax);
            setPaisOrigenDocReceptor(paisDocPax);
            if (paisDocPax) fetchDocumentosPorPais(paisDocPax);
            setTipoDocReceptor(tipoDocPax);
            setNumeroDocReceptor(numDocPax);

            window.localStorage.setItem("NombresReceptorPaso4", nombresPax);
            window.localStorage.setItem("ApellidosReceptorPaso4", apellidosPax);
            window.localStorage.setItem("ReceptorPaisDocumentoPaso4", paisDocPax);
            window.localStorage.setItem("ReceptorTipoDocumentoPaso4", tipoDocPax);
            window.localStorage.setItem("ReceptorNumeroDocumentoPaso4", numDocPax);

            setDocIdentidadTerceroFile(""); setDocIdentidadTerceroExt("");
            setAutorizacionTerceroFile(""); setAutorizacionTerceroExt("");
            setNameDocIdentidadTercero(""); setNameAutorizacionTercero("");
        } else {
            setNombresReceptor(""); setErrorNombresReceptorMsg("");
            setApellidosReceptor(""); setErrorApellidosReceptorMsg("");
            setPaisOrigenDocReceptor("");
            setTipoDocReceptor("");
            setNumeroDocReceptor("");
            setDocumentosPorPaisList([]);
            ["NombresReceptorPaso4", "ApellidosReceptorPaso4", "ReceptorPaisDocumentoPaso4", "ReceptorTipoDocumentoPaso4", "ReceptorNumeroDocumentoPaso4"].forEach(key => window.localStorage.removeItem(key));
        }
    };

    async function fetchPaises() {
        try {
            setActivarLoader(true);
            setMensajeLoader(t('paso2.obteniendoDatos', "Cargando países..."));
            const peticion = await ReembolsoSevices.ObtenerPaises();
            if (peticion.status === 200 && peticion.resp) {
                setPaisesList(peticion.resp);
            } else {
                toast.error(t('paso2.errorPaises', 'Ocurrió un error al cargar la lista de países.'));
            }
        } catch (error) {
            toast.error(t('paso2.errorRed', 'Error de conexión'));
        } finally {
            setActivarLoader(false);
        }
    }

    async function fetchDocumentosPorPais(idPais) {
        if (!idPais) return;
        try {
            setActivarLoader(true);
            setMensajeLoader(t('comunes.espere', "Por favor espere..."));
            const peticion = await ReembolsoSevices.ObtenerDocumentosPorPaises(idPais);
            if (peticion.status === 200 && peticion.resp) {
                setDocumentosPorPaisList(peticion.resp);
            } else {
                setDocumentosPorPaisList([]);
            }
        } catch (error) {
            toast.error(t('paso2.errorDocumentos', 'Ocurrió un error al cargar los documentos.'));
        } finally {
            setActivarLoader(false);
        }
    }

    useEffect(() => {
        const elem = document.querySelector('._3uApM');
        if (elem) elem.style.display = 'none';

        setActivarLoader(true);
        const tieneDocumentosGlobal = window.localStorage.getItem("TieneDocumentos") === "true";
        setFlujoConDocumentosPaso1(tieneDocumentosGlobal);

        fetchPaises();

        const esTitularGuardado = window.localStorage.getItem("EsTitularPaso4") !== "false";
        setEsTitularCuenta(esTitularGuardado);
        setHabilitarCamposReceptor(!esTitularGuardado);

        setNombresReceptor(window.localStorage.getItem("NombresReceptorPaso4") || "");
        setApellidosReceptor(window.localStorage.getItem("ApellidosReceptorPaso4") || "");
        const paisGuardado = window.localStorage.getItem("ReceptorPaisDocumentoPaso4") || "";
        setPaisOrigenDocReceptor(paisGuardado);
        if (paisGuardado) fetchDocumentosPorPais(paisGuardado);
        setTipoDocReceptor(window.localStorage.getItem("ReceptorTipoDocumentoPaso4") || "");
        setNumeroDocReceptor(window.localStorage.getItem("ReceptorNumeroDocumentoPaso4") || "");

        setDireccionPagador(window.localStorage.getItem("DireccionPagadorPaso4") || "");
        setAba(window.localStorage.getItem("AbaPaso4") || "");
        setSwift(window.localStorage.getItem("SwiftPaso4") || "");

        const monedaActual = window.localStorage.getItem("Moneda");
        const formaPagoActual = window.localStorage.getItem("FormaPago");
        if (monedaActual && monedaActual !== monedaNacional && formaPagoActual !== "5" && formaPagoActual !== "2") {
            setMostrarCamposAbaSwift(true);
        } else {
            setMostrarCamposAbaSwift(false);
        }
        setActivarLoader(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFileRead = (event, setFileState, setFileExtState, setNameState) => {
        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name;
        const fileSizeMB = file.size / 1024 / 1024;
        const fileExtension = fileName.split('.').pop().toLowerCase();

        if (fileSizeMB > 0.5) {
            toast.error(t('paso4.errorTamano', 'El archivo no debe superar los 500KB.'));
            event.target.value = null;
            return;
        }
        if (!['pdf', 'jpg', 'jpeg'].includes(fileExtension)) {
            toast.error(t('paso4.errorFormato', 'Formato permitido: PDF o JPG/JPEG.'));
            event.target.value = null;
            return;
        }

        if (setNameState) setNameState(fileName);
        setFileExtState(fileExtension);
        const reader = new FileReader();
        reader.onload = (e) => setFileState(btoa(e.target.result));
        reader.readAsBinaryString(file);
    };

    async function ProcesarReembolso() {
        setActivarLoader(true);
        setMensajeLoader(t('paso4.procesando', "Procesando su solicitud..."));

        const tieneDocumentos = window.localStorage.getItem("TieneDocumentos") === "true";
        const formaPago = window.localStorage.getItem("FormaPago");
        const esTitularStorage = window.localStorage.getItem("EsTitularPaso4") !== "false";
        const moneda = window.localStorage.getItem("Moneda");
        const motivoId = window.localStorage.getItem("Motivo");
        const observaciones = window.localStorage.getItem("Observaciones");
        const correoSolicitante = window.localStorage.getItem("Correo");

        const infoBoletoPaso1 = window.localStorage.getItem("Boleto");
        let dateBoleto = null;
        if (infoBoletoPaso1) {
            const dataP1 = {};
            infoBoletoPaso1.split('; ').forEach(part => {
                const [key, ...valueParts] = part.split(':');
                dataP1[key] = valueParts.join(':');
            });
            if (dataP1.Fecha !== 'N/A') dateBoleto = dataP1.Fecha;
        }
        const identificadorPrincipal = window.localStorage.getItem("Boleto");

        const nombresPax = window.localStorage.getItem("Nombres") || "N/A";
        const apellidosPax = window.localStorage.getItem("Apellidos") || "N/A";
        const origenVuelo = window.localStorage.getItem("Origen") || "N/A";
        const destinoVuelo = window.localStorage.getItem("Destino") || "N/A";
        const tipoPasajero = window.localStorage.getItem("TipoPasajero") || "N/A";
        const paisDocPax = window.localStorage.getItem("PasajeroPaisDocumento") || "N/A";
        const tipoDocPax = window.localStorage.getItem("PasajeroTipoDocumentoSolicitante") || "N/A";
        const numDocPax = window.localStorage.getItem("PasajeroDocumento") || "N/A";

        const nomReceptor = nombresReceptor || nombresPax || "N/A";
        const apeReceptor = apellidosReceptor || apellidosPax || "N/A";
        const paisDocRecep = paisOrigenDocReceptor || paisDocPax || "N/A";
        const tipoDocRecep = tipoDocReceptor || tipoDocPax || "N/A";
        const numDocRecep = numeroDocReceptor || numDocPax || "N/A";
        const dirPagador = direccionPagador || "N/A";
        const codAba = aba || "N/A";
        const codSwift = swift || "N/A";

        let esFormaPagoDirecta = false;
        if (formaPago) {
            esFormaPagoDirecta = FormaPagoC.some(fp => fp.toString() === formaPago);
        }

        if (!esFormaPagoDirecta) {
            if (!nomReceptor || nomReceptor === "N/A" || nomReceptor.trim() === "") { toast.error(t('paso4.reqNombre', 'Debe indicar el nombre del receptor.')); setActivarLoader(false); return; }
            if (!apeReceptor || apeReceptor === "N/A" || apeReceptor.trim() === "") { toast.error(t('paso4.reqApellido', 'Debe indicar el apellido del receptor.')); setActivarLoader(false); return; }
            if (!paisDocRecep || paisDocRecep === "N/A") { toast.error(t('paso4.reqOrigen', 'Seleccione el país emisor del documento.')); setActivarLoader(false); return; }
            if (!tipoDocRecep || tipoDocRecep === "N/A") { toast.error(t('paso4.reqTipoDoc', 'Seleccione el tipo de documento.')); setActivarLoader(false); return; }
            if (!numDocRecep || numDocRecep === "N/A" || numDocRecep.trim() === "") { toast.error(t('paso4.reqNumDoc', 'Ingrese el número de documento.')); setActivarLoader(false); return; }
            if (!dirPagador || dirPagador === "N/A" || dirPagador.length < 10) { toast.error(t('paso4.reqDir', 'Ingrese una dirección válida (mínimo 10 caracteres).')); setActivarLoader(false); return; }

            if (mostrarCamposAbaSwift) {
                if ((!codAba || codAba === "N/A" || codAba.trim() === "") && (!codSwift || codSwift === "N/A" || codSwift.trim() === "")) {
                    toast.error(t('paso4.reqAbaSwift', 'Debe indicar el código ABA o SWIFT.'));
                    setActivarLoader(false); return;
                }
            }

            if (esTitularStorage) {
                if (!docIdentidadTitularFile) { toast.error(t('paso4.reqDocIdentidad', 'Adjunte el documento de identidad.')); setActivarLoader(false); return; }
                if (!cuentaBancariaTitularFile) { toast.error(t('paso4.reqSoporte', 'Adjunte el soporte de cuenta bancaria.')); setActivarLoader(false); return; }
                if (!declaracionJuradaTitularFile) { toast.error(t('paso4.reqDeclaracion', 'Adjunte la declaración jurada.')); setActivarLoader(false); return; }
            } else {
                if (!docIdentidadTerceroFile) { toast.error(t('paso4.reqDocTercero', 'Adjunte el documento del tercero.')); setActivarLoader(false); return; }
                if (!autorizacionTerceroFile) { toast.error(t('paso4.reqAutTercero', 'Adjunte la autorización del tercero.')); setActivarLoader(false); return; }
                if (!cuentaBancariaTitularFile) { toast.error(t('paso4.reqCuentaTercero', 'Adjunte el soporte de cuenta bancaria.')); setActivarLoader(false); return; }
                if (!docIdentidadTitularFile) { toast.error(t('paso4.reqDocPasajero', 'Adjunte el documento del pasajero.')); setActivarLoader(false); return; }
                if (!declaracionJuradaTitularFile) { toast.error(t('paso4.reqDJPasajero', 'Adjunte la declaración jurada.')); setActivarLoader(false); return; }
            }
        }

        const requestPayload = {
            isClaim: "0",
            ClaimDetail: {},
            isComplaint: "0",
            ComplaintDetail: {},
            withTicket: tieneDocumentos ? 1 : 0,
            TicketDetail: {
                numTicket: identificadorPrincipal,
                Date: dateBoleto ? Moment(dateBoleto).toISOString() : Moment().toISOString(),
                Origin: origenVuelo,
                Destination: destinoVuelo,
                NamePAX: nombresPax,
                LastNamePAX: apellidosPax,
                TypePAX: tipoPasajero,
            },
            CurrencyID: moneda,
            ReasonID: motivoId,
            Comment: observaciones,
            Payers: [{
                Name: nombresPax,
                LastName: apellidosPax,
                Email: correoSolicitante,
                PaymentMethodID: formaPago,
                Supports: [
                    ...(autorizacionTerceroFile ? [{ ID: 17, Description: "Autorización de tercero", File: autorizacionTerceroFile, FileExt: autorizacionTerceroExt }] : []),
                    ...(docIdentidadTerceroFile ? [{ ID: 1, Description: "Identidad de tercero", File: docIdentidadTerceroFile, FileExt: docIdentidadTerceroExt }] : []),
                    ...(docIdentidadTitularFile ? [{ ID: 19, Description: "Documento de identidad (Pasajero/Titular)", File: docIdentidadTitularFile, FileExt: docIdentidadTitularExt }] : []),
                    ...(cuentaBancariaTitularFile ? [{ ID: 3, Description: "Cuenta bancaria (Receptor)", File: cuentaBancariaTitularFile, FileExt: cuentaBancariaTitularExt }] : []),
                    ...(declaracionJuradaTitularFile ? [{ ID: 20, Description: "Declaración jurada (Pasajero)", File: declaracionJuradaTitularFile, FileExt: declaracionJuradaTitularExt }] : []),
                ].filter(support => support.File && support.FileExt),
            }],
            Receiver: {
                isPassenger: esTitularStorage ? 1 : 0,
                Name: nomReceptor,
                LastName: apeReceptor,
                DOCCountry: paisDocRecep,
                DOCType: tipoDocRecep,
                DOCNumber: numDocRecep,
                Email: correoSolicitante,
                Address: dirPagador,
                Aba: codAba !== "N/A" ? codAba : "",
                Swift: codSwift !== "N/A" ? codSwift : "",
                Currency: moneda,
                Supports: [],
            }
        };

        try {
            const peticion = await ReembolsoSevices.ProcesarReembolso(JSON.stringify(requestPayload));
            if (peticion.status === 200) {
                if (!peticion.resp.includes("ERROR")) {
                    window.localStorage.clear();
                    setSolicitudEnviada(true);
                    toast((tst) => (
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '400px' }}>
                            <h4 style={{ fontWeight: '700', color: '#565657', marginBottom: '10px', fontSize: '16px' }}>{t('paso4.exitoTitulo', '¡Solicitud registrada!')}</h4>
                            <p style={{ fontSize: '14px', color: '#565657', lineHeight: '1.6', marginBottom: '16px' }}>
                                {t('paso4.exitoMensaje', 'Su requerimiento ha sido registrado. El número de referencia fue enviado a su correo.')}
                            </p>
                            <div style={{ background: '#f5f5f5', borderRadius: '6px', padding: '12px', textAlign: 'center', marginBottom: '16px', border: '1px solid #e0e0e0' }}>
                                <span style={{ fontSize: '13px', color: '#565657' }}>Referencia</span>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: '#565657' }}>{peticion.resp}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #d0d0d0', background: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px', color: '#565657' }} onClick={() => { toast.dismiss(tst.id); window.localStorage.clear(); window.location.href = "https://aviorair.com"; }}>{t('paso4.btnSalir', 'Salir')}</button>
                                <button style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', background: '#cd171a', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }} onClick={() => { toast.dismiss(tst.id); window.localStorage.clear(); window.location.href = "/solicitar"; }}>{t('paso4.btnOtro', 'Nueva solicitud')}</button>
                            </div>
                        </div>
                    ), { duration: Infinity, style: { padding: 0, background: 'transparent' } });
                } else {
                    const errorMatch = peticion.resp.match(/ERROR (\d+)/);
                    const errorCode = errorMatch ? errorMatch[1] : null;
                    const errorMsg = errorCode ? t(`apiErrores.${errorCode}`, t('apiErrores.default') + peticion.resp) : t('apiErrores.default') + peticion.resp;

                    toast((tst) => (
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', maxWidth: '400px' }}>
                            <h4 style={{ fontWeight: '700', color: '#565657', marginBottom: '10px', fontSize: '16px' }}>{t('paso4.errorTitulo', 'Advertencia')}</h4>
                            <p style={{ fontSize: '14px', color: '#565657', lineHeight: '1.6', marginBottom: '16px' }}>{errorMsg}</p>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #d0d0d0', background: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px', color: '#565657' }} onClick={() => { toast.dismiss(tst.id); window.localStorage.clear(); window.location.href = "/"; }}>{t('paso4.btnSalir', 'Salir')}</button>
                                <button style={{ flex: 1, padding: '10px', borderRadius: '6px', border: 'none', background: '#cd171a', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }} onClick={() => toast.dismiss(tst.id)}>{t('paso4.btnReintentar', 'Reintentar')}</button>
                            </div>
                        </div>
                    ), { duration: Infinity, style: { padding: 0, background: 'transparent' } });
                }
            } else {
                toast.error(t('paso2.errorRed', 'Error de conexión'));
            }
        } catch (error) {
            toast.error(t('paso2.errorRed', 'Error de conexión'));
        } finally {
            setActivarLoader(false);
        }
    }

    const getInputStyle = (fieldName) => ({
        ...styles.formInput,
        borderColor: focusedField === fieldName ? '#cd171a' : '#d0d0d0',
    });

    const getSelectStyle = (fieldName) => ({
        ...styles.formSelect,
        borderColor: focusedField === fieldName ? '#cd171a' : '#d0d0d0',
    });

    return (
        <>
            <LoaderScreen props={ActivarLoader} mensaje={MensajeLoader} />
            <div style={styles.pageContainer}>

                {/* HEADER */}
                <div style={styles.pageHeader}>
                    <h2 style={styles.pageTitle}>{t('paso4.titulo', 'Consignación de documentos')}</h2>
                    <p style={styles.pageSubtitle}>
                        {t('paso2.subtitulo', 'Todos los campos marcados con asterisco (*) son obligatorios.')}
                    </p>
                </div>

                {/* TITULARIDAD */}
                <div style={styles.sectionCard}>
                    <h3 style={styles.sectionTitle}>
                        {t('paso4.titularidadTitle', 'Titularidad de la cuenta')}
                    </h3>
                    <p style={styles.sectionDescription}>
                        {t('paso4.titularidadDesc', '¿La cuenta bancaria donde se depositará el reembolso pertenece al pasajero solicitante?')}
                    </p>
                    <hr style={styles.divider} />

                    <div style={styles.ownershipToggle}>
                        <div
                            style={styles.ownershipOption(esTitularCuenta === true)}
                            onClick={() => handleEsTitularCuentaChange({ target: { value: 'true' } })}
                        >
                            <span style={styles.ownershipLabel(esTitularCuenta === true)}>
                                {t('paso4.si', 'Sí, soy el titular')}
                            </span>
                            <span style={styles.ownershipDesc(esTitularCuenta === true)}>
                                {t('paso4.siDesc', 'La cuenta bancaria me pertenece')}
                            </span>
                        </div>

                        <div
                            style={styles.ownershipOption(esTitularCuenta === false)}
                            onClick={() => handleEsTitularCuentaChange({ target: { value: 'false' } })}
                        >
                            <span style={styles.ownershipLabel(esTitularCuenta === false)}>
                                {t('paso4.no', 'No, es un tercero')}
                            </span>
                            <span style={styles.ownershipDesc(esTitularCuenta === false)}>
                                {t('paso4.noDesc', 'Otra persona recibirá el reembolso')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* DATOS DEL RECEPTOR */}
                <div style={styles.sectionCard}>
                    <h3 style={styles.sectionTitle}>
                        {t('paso4.datosReceptor', 'Datos del receptor del reembolso')}
                    </h3>
                    <p style={styles.sectionDescription}>
                        {esTitularCuenta
                            ? t('paso4.subTitular', 'Los datos del pasajero solicitante se usarán como receptor.')
                            : t('paso4.subTercero', 'Ingrese los datos de la persona que recibirá el reembolso.')}
                    </p>
                    <hr style={styles.divider} />

                    {esTitularCuenta && (
                        <div style={styles.infoBanner}>
                            {t('paso4.infoTitular', 'Los datos del pasajero registrado anteriormente se han copiado automáticamente. Puede modificarlos si es necesario.')}
                        </div>
                    )}

                    <div className="row">
                        <div className="col-12 col-md-6">
                            <FormField label={t('paso4.nombres', 'Nombre(s) del receptor')} required error={errorNombresReceptorMsg}>
                                <input
                                    value={nombresReceptor}
                                    onChange={(e) => handleNombresReceptorChange(e.target.value)}
                                    onFocus={() => setFocusedField('nombres')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('nombres')}
                                    type="text"
                                    placeholder={t('paso2.placeholderNombre', 'Ej: Juan Carlos')}
                                />
                            </FormField>
                        </div>
                        <div className="col-12 col-md-6">
                            <FormField label={t('paso4.apellidos', 'Apellido(s) del receptor')} required error={errorApellidosReceptorMsg}>
                                <input
                                    value={apellidosReceptor}
                                    onChange={(e) => handleApellidosReceptorChange(e.target.value)}
                                    onFocus={() => setFocusedField('apellidos')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('apellidos')}
                                    type="text"
                                    placeholder={t('paso2.placeholderApellido', 'Ej: García López')}
                                />
                            </FormField>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-md-4">
                            <FormField label={t('paso2.paisDoc', 'País emisor del documento')} required>
                                <select
                                    style={getSelectStyle('pais')}
                                    value={paisOrigenDocReceptor}
                                    onChange={(e) => handlePaisOrigenDocReceptorChange(e.target.value)}
                                    onFocus={() => setFocusedField('pais')}
                                    onBlur={() => setFocusedField(null)}
                                >
                                    <option value="">{t('comunes.seleccione', '— Seleccione —')}</option>
                                    {paisesList && paisesList.length > 0 && paisesList.map(pais => (
                                        <option key={pais.ID} value={pais.ID}>
                                            {t(`backend.paises.${pais.Description.trim()}`, pais.Description.trim())}
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                        </div>
                        <div className="col-12 col-md-4">
                            <FormField label={t('paso2.tipoDoc', 'Tipo de documento')} required>
                                <select
                                    style={getSelectStyle('tipoDoc')}
                                    value={tipoDocReceptor}
                                    onChange={(e) => handleTipoDocReceptorChange(e.target.value)}
                                    onFocus={() => setFocusedField('tipoDoc')}
                                    onBlur={() => setFocusedField(null)}
                                >
                                    <option value="">{t('comunes.seleccione', '— Seleccione —')}</option>
                                    {documentosPorPaisList && documentosPorPaisList.length > 0 && documentosPorPaisList.map(doc => (
                                        <option key={doc.ID} value={doc.ID}>{t(`backend.documentos.${doc.Description}`, doc.Description)}</option>
                                    ))}
                                </select>
                            </FormField>
                        </div>
                        <div className="col-12 col-md-4">
                            <FormField label={t('paso2.numeroDoc', 'Número de documento')} required>
                                <input
                                    value={numeroDocReceptor}
                                    onChange={(e) => handleNumeroDocReceptorChange(e.target.value)}
                                    onFocus={() => setFocusedField('numDoc')}
                                    onBlur={() => setFocusedField(null)}
                                    style={getInputStyle('numDoc')}
                                    type="text"
                                    placeholder={t('paso2.placeholderNumDoc', 'Ej: V-12345678')}
                                />
                            </FormField>
                        </div>
                    </div>

                    <FormField
                        label={t('paso4.direccion', 'Dirección del titular de la cuenta bancaria')}
                        required
                        hint={t('paso4.indicacionDir', 'Indique la dirección completa: país, estado, ciudad, avenida, calle, etc.')}
                    >
                        <input
                            value={direccionPagador}
                            onChange={(e) => handleDireccionPagadorChange(e.target.value)}
                            onFocus={() => setFocusedField('direccion')}
                            onBlur={() => setFocusedField(null)}
                            style={{ ...getInputStyle('direccion'), padding: '14px' }}
                            type="text"
                            placeholder={t('paso4.placeholderDir', 'País, estado, ciudad, avenida, calle, edificio/casa')}
                        />
                    </FormField>
                </div>

                {/* ABA / SWIFT */}
                {mostrarCamposAbaSwift && (
                    <div style={styles.sectionCard}>
                        <h3 style={styles.sectionTitle}>
                            {t('paso4.intlTitle', 'Datos bancarios internacionales')}
                        </h3>
                        <p style={styles.sectionDescription}>
                            {t('paso4.intlDesc', 'Para transferencias internacionales debe proporcionar al menos uno de los siguientes códigos.')}
                        </p>
                        <hr style={styles.divider} />

                        <div className="row">
                            <div className="col-12 col-md-6">
                                <FormField label="ABA / Routing" hint={t('paso4.siAplica', 'Requerido si aplica')}>
                                    <input
                                        onChange={(e) => handleAbaChange(e.target.value)}
                                        onFocus={() => setFocusedField('aba')}
                                        onBlur={() => setFocusedField(null)}
                                        style={getInputStyle('aba')}
                                        type="text"
                                        placeholder="Ej: 021000021"
                                        value={aba}
                                    />
                                </FormField>
                            </div>
                            <div className="col-12 col-md-6">
                                <FormField label="SWIFT / BIC" hint={t('paso4.siAplica', 'Requerido si aplica')}>
                                    <input
                                        onChange={(e) => handleSwiftChange(e.target.value)}
                                        onFocus={() => setFocusedField('swift')}
                                        onBlur={() => setFocusedField(null)}
                                        style={getInputStyle('swift')}
                                        type="text"
                                        placeholder="Ej: CHASUS33"
                                        value={swift}
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>
                )}

                {/* DOCUMENTOS */}
                <div style={styles.sectionCard}>
                    <h3 style={styles.sectionTitle}>
                        <span style={styles.required}>* </span>
                        {t('paso4.docConsignar', 'Documentos a consignar')}
                    </h3>
                    <p style={styles.sectionDescription}>
                        {t('paso4.docDesc', 'Adjunte los documentos solicitados en formato PDF o JPG/JPEG (máx. 500KB por archivo).')}
                    </p>
                    <hr style={styles.divider} />

                    <div style={styles.warningBanner}>
                        {t('paso4.formatoDoc', 'Formatos aceptados: PDF, JPG, JPEG — Tamaño máximo: 500KB por archivo')}
                    </div>

                    {/* TERCERO */}
                    {!esTitularCuenta && (
                        <Fragment>
                            <div style={styles.docGroupCard}>
                                <div style={styles.docGroupTitle}>
                                    {t('paso4.docTerceroTitle', 'Documentos del tercero (receptor del reembolso)')}
                                </div>
                                <div className="row align-items-stretch">
                                    <div className="col-12 col-md-4 mb-3 d-flex flex-column">
                                        <FileUploadCard
                                            label={t('paso4.docTercero', 'Documento de identidad del tercero')}
                                            fileName={nameDocIdentidadTercero}
                                            required={true}
                                            t={t}
                                            onChange={(e) => handleFileRead(e, setDocIdentidadTerceroFile, setDocIdentidadTerceroExt, setNameDocIdentidadTercero)}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4 mb-3 d-flex flex-column">
                                        <FileUploadCard
                                            label={t('paso4.autTercero', 'Carta de autorización')}
                                            fileName={nameAutorizacionTercero}
                                            required={true}
                                            t={t}
                                            onChange={(e) => handleFileRead(e, setAutorizacionTerceroFile, setAutorizacionTerceroExt, setNameAutorizacionTercero)}
                                            downloadFormatUrl={`${process.env.PUBLIC_URL}/Descargas/nuevas2/CARTA DE AUTORIZACIÓN A TERCERO (V2.0).pdf`}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4 mb-3 d-flex flex-column">
                                        <FileUploadCard
                                            label={t('paso4.soporteTercero', 'Soporte cuenta bancaria')}
                                            fileName={nameCuentaBancariaTitular}
                                            required={true}
                                            t={t}
                                            onChange={(e) => handleFileRead(e, setCuentaBancariaTitularFile, setCuentaBancariaTitularExt, setNameCuentaBancariaTitular)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={styles.docGroupCard}>
                                <div style={styles.docGroupTitle}>
                                    {t('paso4.docPasajeroTercero', 'Documentos del pasajero solicitante')}
                                </div>
                                <div className="row align-items-stretch">
                                    <div className="col-12 col-md-6 mb-3 d-flex flex-column">
                                        <FileUploadCard
                                            label={t('paso4.docPasajero', 'Documento de identidad')}
                                            fileName={nameDocIdentidadTitular}
                                            required={true}
                                            t={t}
                                            onChange={(e) => handleFileRead(e, setDocIdentidadTitularFile, setDocIdentidadTitularExt, setNameDocIdentidadTitular)}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 mb-3 d-flex flex-column">
                                        <FileUploadCard
                                            label={t('paso4.djPasajero', 'Declaración jurada')}
                                            fileName={nameDeclaracionJuradaTitular}
                                            required={true}
                                            t={t}
                                            onChange={(e) => handleFileRead(e, setDeclaracionJuradaTitularFile, setDeclaracionJuradaTitularExt, setNameDeclaracionJuradaTitular)}
                                            downloadFormatUrl={`${process.env.PUBLIC_URL}/Descargas/nuevas2/CARTA DE DECLARACIÓN JURADA (V2.0).pdf`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    )}

                    {/* TITULAR */}
                    {esTitularCuenta && (
                        <div style={styles.docGroupCard}>
                            <div style={styles.docGroupTitle}>
                                {t('paso4.docTitular', 'Documentos del pasajero titular')}
                            </div>
                            <div className="row align-items-stretch">
                                <div className="col-12 col-md-4 mb-3 d-flex flex-column">
                                    <FileUploadCard
                                        label={t('paso4.docPasajero', 'Documento de identidad')}
                                        fileName={nameDocIdentidadTitular}
                                        required={true}
                                        t={t}
                                        onChange={(e) => handleFileRead(e, setDocIdentidadTitularFile, setDocIdentidadTitularExt, setNameDocIdentidadTitular)}
                                    />
                                </div>
                                <div className="col-12 col-md-4 mb-3">
                                    <FileUploadCard
                                        label={t('paso4.soporteTitular', 'Soporte cuenta bancaria')}
                                        fileName={nameCuentaBancariaTitular}
                                        required={true}
                                        t={t}
                                        onChange={(e) => handleFileRead(e, setCuentaBancariaTitularFile, setCuentaBancariaTitularExt, setNameCuentaBancariaTitular)}
                                    />
                                </div>
                                <div className="col-12 col-md-4 mb-3">
                                    <FileUploadCard
                                        label={t('paso4.djPasajero', 'Declaración jurada')}
                                        fileName={nameDeclaracionJuradaTitular}
                                        required={true}
                                        t={t}
                                        onChange={(e) => handleFileRead(e, setDeclaracionJuradaTitularFile, setDeclaracionJuradaTitularExt, setNameDeclaracionJuradaTitular)}
                                        downloadFormatUrl={`${process.env.PUBLIC_URL}/Descargas/nuevas2/CARTA DE DECLARACIÓN JURADA (V2.0).pdf`}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* SUBMIT */}
                <div style={styles.submitSection}>
                    <button
                        onClick={ProcesarReembolso}
                        disabled={ActivarLoader || solicitudEnviada}
                        style={{
                            ...styles.submitButton,
                            ...(solicitudEnviada ? { background: '#ccc', cursor: 'not-allowed' } : {})
                        }}
                        onMouseEnter={(e) => { if (!solicitudEnviada) e.currentTarget.style.background = '#b81418'; }}
                        onMouseLeave={(e) => { if (!solicitudEnviada) e.currentTarget.style.background = '#cd171a'; }}
                        type="button"
                    >
                        {solicitudEnviada ? t('paso4.enviado', 'Solicitud enviada') : t('paso4.btnEnviar', 'Enviar solicitud de reembolso')}
                    </button>
                    <div style={styles.submitHint}>
                        {t('paso4.submitHint', 'Su información se envía de forma segura')}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Paso4;